import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';

export type RequirementCadence =
  | ''
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'semiannual'
  | 'ongoing';

export type RequirementStatus =
  | 'not_started'
  | 'on_track'
  | 'at_risk'
  | 'met'
  | 'missed';

export interface BrandRequirement {
  id: number;
  program: number;
  title: string;
  pillar: string;
  detail: string;
  cadence: RequirementCadence;
  is_gate: boolean;
  weight: string;
  target: string;
  status: RequirementStatus;
  notes: string;
  sort_order: number;
}

export interface BrandProgram {
  id: number;
  brand: string;
  name: string;
  reference_period: string;
  effective_date: string | null;
  front_discount: string;
  rebate_ceiling: string;
  eligibility_threshold: string;
  settlement: string;
  notes: string;
  requirements: BrandRequirement[];
}

const listKey = (brandId: string) => ['brand-programs', brandId] as const;

export const usePrograms = (brandId: string | undefined) =>
  useQuery<BrandProgram[]>({
    queryKey: listKey(brandId ?? ''),
    queryFn: async () => {
      const res = await axios.get(`api/brands/${brandId}/programs/`);
      return res.data;
    },
    enabled: !!brandId,
  });

export const useCreateProgram = (brandId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<BrandProgram>): Promise<BrandProgram> => {
      const res = await axios.post(`api/brands/${brandId}/programs/`, payload);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  });
};

export const usePatchProgram = (brandId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<BrandProgram>;
    }): Promise<BrandProgram> => {
      const res = await axios.patch(`api/brands/${brandId}/programs/${id}/`, patch);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  });
};

export const useDeleteProgram = (brandId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`api/brands/${brandId}/programs/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  });
};

export const useCreateRequirement = (brandId: string, programId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<BrandRequirement>): Promise<BrandRequirement> => {
      const res = await axios.post(
        `api/brands/${brandId}/programs/${programId}/requirements/`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  });
};

export const usePatchRequirement = (brandId: string, programId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<BrandRequirement>;
    }): Promise<BrandRequirement> => {
      const res = await axios.patch(
        `api/brands/${brandId}/programs/${programId}/requirements/${id}/`,
        patch,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  });
};

export const useDeleteRequirement = (brandId: string, programId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(
        `api/brands/${brandId}/programs/${programId}/requirements/${id}/`,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  });
};
